<script setup lang="ts">
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { TaskAssigneeSnapshot } from '~/types/models';

const props = withDefaults(defineProps<{
    assignees?: TaskAssigneeSnapshot[],
    emptyLabel?: string,
    maxVisible?: number,
    compact?: boolean,
    showLabel?: boolean,
}>(), {
    assignees: () => [],
    emptyLabel: 'Unassigned',
    maxVisible: 3,
    compact: false,
    showLabel: true,
});

const visibleAssignees = computed(() => props.assignees.slice(0, props.maxVisible));
const overflowCount = computed(() => Math.max(0, props.assignees.length - visibleAssignees.value.length));

function initials(label: string) {
    return label
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() || '')
        .join('');
}
</script>

<template>
    <span v-if="!assignees.length" class="text-sm text-muted-foreground">{{ emptyLabel }}</span>

    <HoverCard v-else>
        <HoverCardTrigger as-child>
            <button
                type="button"
                :class="['inline-flex items-center text-left', compact ? 'gap-1.5' : 'gap-2']"
            >
                <span class="flex -space-x-2">
                    <Avatar
                        v-for="assignee in visibleAssignees"
                        :key="assignee.id"
                        :class="compact ? 'h-5 w-5 border-2 border-background' : 'h-7 w-7 border-2 border-background'"
                    >
                        <AvatarImage v-if="assignee.avatarURL" :src="assignee.avatarURL" :alt="assignee.label" />
                        <AvatarFallback class="bg-primary/15 text-[10px] font-semibold text-primary">
                            {{ initials(assignee.label) }}
                        </AvatarFallback>
                    </Avatar>

                    <span
                        v-if="overflowCount > 0"
                        :class="[
                            'inline-flex items-center justify-center rounded-full border-2 border-background bg-muted font-medium text-muted-foreground',
                            compact ? 'h-5 w-5 text-[9px]' : 'h-7 w-7 text-xs',
                        ]"
                    >
                        +{{ overflowCount }}
                    </span>
                </span>

                <span v-if="!compact && showLabel" class="truncate text-sm text-foreground">
                    {{ assignees.length === 1 ? assignees[0]?.label : `${assignees.length} assignees` }}
                </span>
            </button>
        </HoverCardTrigger>

        <HoverCardContent class="w-64">
            <div class="space-y-2">
                <div>
                    <p class="text-sm font-medium">{{ assignees.length === 1 ? 'Assigned teammate' : 'Assigned teammates' }}</p>
                    <p class="text-xs text-muted-foreground">Everyone currently responsible for this task.</p>
                </div>

                <div class="space-y-2">
                    <div v-for="assignee in assignees" :key="assignee.id" class="flex items-center gap-2">
                        <Avatar class="h-7 w-7">
                            <AvatarImage v-if="assignee.avatarURL" :src="assignee.avatarURL" :alt="assignee.label" />
                            <AvatarFallback class="bg-primary/15 text-[10px] font-semibold text-primary">
                                {{ initials(assignee.label) }}
                            </AvatarFallback>
                        </Avatar>
                        <span class="text-sm text-foreground">{{ assignee.label }}</span>
                    </div>
                </div>
            </div>
        </HoverCardContent>
    </HoverCard>
</template>
